const RECENTLY_VIEWED_STORAGE_KEY = 'asmota-recently-viewed';
const RECENTLY_VIEWED_MAX_ITEMS = 12;

function getRecentlyViewed() {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function recordRecentlyViewed(handle) {
  if (!handle) return;
  let list = getRecentlyViewed().filter((h) => h !== handle);
  list.unshift(handle);
  list = list.slice(0, RECENTLY_VIEWED_MAX_ITEMS);
  try {
    localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    /* storage unavailable, ignore */
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const dataEl = document.getElementById('RecentlyViewedCurrentProduct');
  if (dataEl) {
    try {
      const handle = JSON.parse(dataEl.textContent);
      recordRecentlyViewed(handle);
    } catch (e) {
      /* ignore malformed data */
    }
  }
});

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    this.excludeHandle = this.dataset.excludeHandle || '';
    this.limit = parseInt(this.dataset.limit || '8', 10);
    this.batchSize = parseInt(this.dataset.batchSize || '8', 10) || 8;
    this.handles = [];
    this.nextIndex = 0;
    this.loading = false;
    this.scheduleRender();
  }

  scheduleRender() {
    if (!('IntersectionObserver' in window)) {
      this.render();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.render();
    }, { rootMargin: '400px 0px' });
    this.observer.observe(this);
  }

  async render() {
    this.handles = getRecentlyViewed()
      .filter((h) => h !== this.excludeHandle)
      .slice(0, this.limit);

    const section = this.closest('.recently-viewed-products__section');
    const list = this.querySelector('[data-recently-viewed-list]');
    if (this.handles.length === 0 || !list) {
      section?.setAttribute('hidden', '');
      return;
    }

    const nextButton = this.querySelector('.recently-viewed-products__arrow--next');
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        const track = this.querySelector('.recently-viewed-products__track');
        const wasAtEnd = track && track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
        this.loadNextBatch(wasAtEnd);
      });
    }

    // Reuse the server-rendered skeletons as the first batch's placeholders.
    const skeletons = Array.from(list.querySelectorAll('.rv-card--skeleton'))
      .map((skeleton) => skeleton.closest('.grid__item'))
      .filter(Boolean);
    const firstBatchSize = Math.min(this.batchSize, this.handles.length);
    skeletons.slice(firstBatchSize).forEach((item) => item.remove());
    skeletons.slice(0, firstBatchSize).forEach((item) => {
      item.querySelector('.rv-card--skeleton')?.appendChild(this.createSpinner());
    });

    await this.loadNextBatch(false, skeletons.slice(0, firstBatchSize));
    while (!list.querySelector('.rv-card:not(.rv-card--skeleton)') && this.hasMore()) {
      await this.loadNextBatch(false);
    }

    if (!list.querySelector('.rv-card:not(.rv-card--skeleton)')) {
      section?.setAttribute('hidden', '');
      return;
    }

    section?.removeAttribute('hidden');
    this.updateArrowVisibility();
    this.resizeHandler = () => this.updateArrowVisibility();
    window.addEventListener('resize', this.resizeHandler);
  }

  createSpinner() {
    const spinner = document.createElement('span');
    spinner.className = 'rv-card__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    return spinner;
  }

  createPlaceholder() {
    const item = document.createElement('li');
    item.className = 'grid__item';
    item.setAttribute('aria-hidden', 'true');
    const skeleton = document.createElement('div');
    skeleton.className = 'rv-card rv-card--skeleton';
    skeleton.appendChild(this.createSpinner());
    item.appendChild(skeleton);
    return item;
  }

  // Keep a spinner over each new card's image until that image finishes loading.
  showSpinnerUntilImageLoads(item) {
    const media = item.querySelector('.rv-card__media');
    const image = item.querySelector('.rv-card__media img');
    if (!media || !image || (image.complete && image.naturalWidth > 0)) return;

    const spinner = this.createSpinner();
    media.appendChild(spinner);
    const done = () => spinner.remove();
    image.addEventListener('load', done, { once: true });
    image.addEventListener('error', done, { once: true });
  }

  hasMore() {
    return this.nextIndex < this.handles.length;
  }

  async loadNextBatch(scrollToNew, existingPlaceholders) {
    const list = this.querySelector('[data-recently-viewed-list]');
    const track = this.querySelector('.recently-viewed-products__track');
    if (!list || this.loading || !this.hasMore()) return;

    this.loading = true;
    this.dataset.loading = 'true';
    const batch = this.handles.slice(this.nextIndex, this.nextIndex + this.batchSize);
    this.nextIndex += batch.length;
    this.dataset.hasMore = this.hasMore() ? 'true' : 'false';

    let placeholders = existingPlaceholders;
    if (!placeholders || !placeholders.length) {
      placeholders = batch.map(() => {
        const placeholder = this.createPlaceholder();
        list.appendChild(placeholder);
        return placeholder;
      });
      if (scrollToNew && track) {
        track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
      }
    }
    this.notifyControls();

    try {
      const cards = await Promise.all(batch.map((handle) => this.fetchCard(handle)));
      cards.forEach((cardHTML, index) => {
        const placeholder = placeholders[index];
        if (!placeholder) return;
        if (!cardHTML) {
          placeholder.remove();
          return;
        }
        const li = document.createElement('li');
        li.className = 'grid__item';
        li.innerHTML = cardHTML;
        placeholder.replaceWith(li);
        this.showSpinnerUntilImageLoads(li);
      });
    } finally {
      placeholders.forEach((placeholder) => placeholder.isConnected && placeholder.remove());
      this.loading = false;
      delete this.dataset.loading;
      this.notifyControls();
    }
  }

  notifyControls() {
    this.querySelector('.recently-viewed-products__track')?.dispatchEvent(new Event('scroll'));
  }

  updateArrowVisibility() {
    const track = this.querySelector('.recently-viewed-products__track');
    const arrow = this.querySelector('.recently-viewed-products__arrow');
    if (!track || !arrow) return;
    const hasOverflow = track.scrollWidth > track.clientWidth + 1 || this.hasMore();
    arrow.style.display = hasOverflow ? 'inline-flex' : 'none';
  }

  async fetchCard(handle) {
    try {
      const res = await fetch(`/products/${handle}?view=recently-viewed-card`);
      if (!res.ok) return null;
      const text = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const card = doc.querySelector('.rv-card');
      return card ? card.outerHTML : null;
    } catch (e) {
      return null;
    }
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
